import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Visibility } from '@material-ui/icons';
import { format, parseISO } from 'date-fns';

import { UnidataTable, Modal } from '~/components';
import { t, can, number } from '~/helpers';
import { handleExportGeneric } from '~/helpers/exportReports';
import {
  dataInRequest,
  deleteData,
  findData,
  recordFiltering,
} from '~/store/modules/notafiscalrecebida/actions';

import { Toolbar, Details } from './components';
import MyForm from './form';

function Notafiscalrecebida() {
  const delayRefresh = 1;

  const dispatch = useDispatch();

  const redux = useSelector((state) => state.notafiscalrecebida);

  const status = useMemo(
    () => [
      { label: 'L', value: t('s.geral.opcoes.liberado') },
      { label: 'R', value: t('s.geral.opcoes.EmRecebimento') },
      { label: 'F', value: t('s.geral.opcoes.Finalizada') },
      { label: 'C', value: t('s.geral.opcoes.cancelado') },
    ],
    []
  );

  const statusprodutoscapendente = useMemo(
    () => [
      { label: 'S', value: t('s.geral.opcoes.sim') },
      { label: 'N', value: t('s.geral.opcoes.nao') },
    ],
    []
  );

  const modalRef = useRef(null);

  const buttons = useMemo(
    () => [
      {
        label: 'Fechar',
        color: 'primary',
        variant: 'container',
        autoFocus: true,
        callback: () => modalRef.current.handleCloseModal(),
      },
    ],
    []
  );

  useEffect(() => {
    const intervalId = setInterval(() => {
      handleGetDispatchRedux(redux?.params);
    }, delayRefresh * 1000 * 60);

    return () => clearInterval(intervalId);
  }, [redux.params]);

  const handleGetDispatchRedux = useCallback((query) => {
    dispatch(recordFiltering(query));
  }, []);

  const header = useMemo(
    () => [
      {
        id: 'emissaonfe_inicial',
        label: t(
          's.operacao.notafiscalrecebida.dataemissaonfnotafiscalrecebida_inicial'
        ),
        formatted: (value) => format(parseISO(value), 'dd/MM/yyyy HH:mm'),
        filter: {
          type: 'initialdatetime',
          defaultValue: format(new Date(), 'dd/MM/yyyy 00:00:00'),
        },
      },
      {
        id: 'emissaonfe_final',
        label: t(
          's.operacao.notafiscalrecebida.dataemissaonfnotafiscalrecebida_final'
        ),
        formatted: (value) => format(parseISO(value), 'dd/MM/yyyy HH:mm'),
        filter: {
          type: 'finaldatetime',
          defaultValue: format(new Date(), 'dd/MM/yyyy 23:59:59'),
        },
      },
      {
        id: 'nfnfe',
        label: t('s.operacao.notafiscalrecebida.nfnotafiscalrecebida'),
        filter: { type: 'string' },
      },
      {
        id: 'idprodutonfestr',
        label: t('s.operacao.notafiscalrecebida.produtonfe'),
        filter: { type: 'string' },
      },
      {
        id: 'idprodutonscastr',
        label: t('s.operacao.notafiscalrecebida.idprodutonscastr'),
        filter: { type: 'string' },
      },
      {
        id: 'emissaonfe',
        label: t(
          's.operacao.notafiscalrecebida.dataemissaonfnotafiscalrecebida'
        ),
        formatted: (value) => format(parseISO(value), 'dd/MM/yyyy HH:mm'),
      },
      {
        id: 'qcomnfe',
        label: t('s.operacao.notafiscalrecebida.volumenfnotafiscalrecebida'),
        align: 'right',
        formatted: (value) => number(value, 2),
      },
      {
        id: 'statusnfe',
        label: t('s.cadastro.contrato.statuscontrato'),
        formatted: (value) => status.find((s) => s.label === value).value || '',
        filter: { type: 'select', options: status },
      },
      {
        id: 'statusprodutoscapendente',
        label: t('s.operacao.notafiscalrecebida.statusprodutoscapendente'),
        formatted: (value) =>
          statusprodutoscapendente.find((s) => s.label === value).value || '',
        filter: { type: 'select', options: statusprodutoscapendente },
      },
      {
        id: 'cnpjdistribuidoranfe',
        label: t('s.operacao.notafiscalrecebida.cnpjdistribuidoranfe'),
        align: 'right',
        filter: { type: 'string' },
      },
      {
        id: 'cnpjdestinatarionfe',
        label: t('s.operacao.notafiscalrecebida.cnpjdestinatarionfe'),
        align: 'right',
        filter: { type: 'string' },
      },
    ],
    []
  );

  const handleOpenModal = useCallback((row) => {
    modalRef.current.handleOpenModal({ data: row });
  }, []);

  const actions = useMemo(
    () => [
      {
        title: 'Exibir Detalhe',
        color: 'default',
        icon: <Visibility />,
        action: handleOpenModal,
      },
    ],
    []
  );

  const fieldlist = [
    'emissaonfe_inicial',
    'emissaonfe_final',
    'nfnfe',
    'emissaonfe',
    'idprodutonfestr',
    'idprodutonscastr',
    'qcomnfe',
    'statusnfe',
    'statusprodutoscapendente',
    'cnpjdistribuidoranfe',
    'cnpjdestinatarionfe',
  ];

  const substituirHeader = {
    emissaonfe_inicial: t(
      's.operacao.notafiscalrecebida.dataemissaonfnotafiscalrecebida_inicial'
    ),
    emissaonfe_final: t(
      's.operacao.notafiscalrecebida.dataemissaonfnotafiscalrecebida_final'
    ),
    nfnfe: t('s.operacao.notafiscalrecebida.nfnotafiscalrecebida'),
    emissaonfe: t(
      's.operacao.notafiscalrecebida.dataemissaonfnotafiscalrecebida'
    ),
    idprodutonfestr: t('s.operacao.notafiscalrecebida.produtonfe'),
    idprodutonscastr: t('s.operacao.notafiscalrecebida.idprodutonscastr'),
    qcomnfe: t('s.operacao.notafiscalrecebida.volumenfnotafiscalrecebida'),
    statusnfe: t('s.cadastro.contrato.statuscontrato'),
    statusprodutoscapendente: t(
      's.operacao.notafiscalrecebida.statusprodutoscapendente'
    ),
    cnpjdistribuidoranfe: t(
      's.operacao.notafiscalrecebida.cnpjdistribuidoranfe'
    ),
    cnpjdestinatarionfe: t('s.operacao.notafiscalrecebida.cnpjdestinatarionfe'),
  };

  return (
    <>
      <UnidataTable
        create={false}
        delete={false}
        update
        fixed
        redux={redux}
        header={header}
        read={recordFiltering}
        find={findData}
        form={MyForm}
        actions={actions}
        toolbar={Toolbar}
        keys="idnfe"
        detach="idnfe"
        pdf
        excel
        onExport={(formato) =>
          handleExportGeneric({
            endpoint: 'notafiscalrecebida',
            formato,
            fields: fieldlist,
            headers: substituirHeader,
            redux,
            ordination: redux.params.ordination,
            orderfield: redux.params.orderfield,
            title: 'Relatório de NF Recebidas',
            substituirValores: {
              statusnfe: {
                L: t('s.geral.opcoes.liberado'),
                R: t('s.geral.opcoes.EmRecebimento'),
                F: t('s.geral.opcoes.Finalizada'),
                C: t('s.geral.opcoes.cancelado'),
              },
              statusprodutoscapendente: {
                S: t('s.geral.opcoes.sim'),
                N: t('s.geral.opcoes.nao'),
              },
            },
            ignorarFormatacao: {
              nfnfe: true,
            },
          })
        }
      />
      {/* <UnidataTable
        create={false}
        delete={false}
        update={true}
        fixed
        redux={redux}
        header={header}
        read={dataInRequest}
        find={findData}
        form={MyForm}
        actions={actions}
        toolbar={Toolbar}
        keys="idnfe"
        detach="idnfe"
      /> */}

      <Modal
        disableBackdropClick
        disableEscapeKeyDown
        ref={modalRef}
        actions={buttons}
        title="NF Recebidas"
        component={Details}
      />
    </>
  );
}

export default Notafiscalrecebida;
